import { ImageInline, ClipboardLinksDecode, CustomFontColor, CustomFontFamily } from '../../../imports/ck-editor-plugins';
import { CKPlugins } from '../../../imports/ck-editor-plugins/constants/constants';
import CustomBackgroundFontColor from '../../../imports/ck-editor-plugins/font-background-color';
import { CUSTOM_BACKGROUND_COLORS } from '../../../imports/ck-editor-plugins/font-background-color/map/color.map';
import { CUSTOM_COLORS } from '../../../imports/ck-editor-plugins/font-color/map/color.map';
import { CUSTOM_FONT_FAMILY } from '../../../imports/ck-editor-plugins/font-family/map/font-family.map';

const {
    AccessibilityHelp,
    AutoLink,
    Autosave,
    BlockQuote,
    Bold,
    Essentials,
    Heading,
    Italic,
    Link,
    List,
    Paragraph,
    SelectAll,
    SpecialCharacters,
    Strikethrough,
    Underline,
    Undo,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    PictureEditing,
    Clipboard,
    FontBackgroundColor,
    Alignment,
    Table,
    TableToolbar,
    Markdown,
    Code,
    CodeBlock,
    HorizontalLine,
    Autoformat,
    PasteFromMarkdownExperimental
} = window.ClassicEditor;

const basePlugins = [
    AccessibilityHelp,
    AutoLink,
    Autosave,
    BlockQuote,
    Bold,
    Essentials,
    Heading,
    Italic,
    Link,
    List,
    Paragraph,
    SelectAll,
    SpecialCharacters,
    Strikethrough,
    Underline,
    Undo,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    PictureEditing,
    Clipboard,
    FontBackgroundColor,
    Alignment,
    Table,
    TableToolbar
];

const defaultConfig = {
    toolbar: {
        items: [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'strikethrough',
            '|',
            CKPlugins.CustomFontColor,
            CKPlugins.CustomFontBackgroundColor,
            CKPlugins.CustomFontFamily,
            '|',
            'numberedList',
            'bulletedList',
            'blockQuote',
            '|',
            'link',
            '|',
            'alignment',
            '|',
            'undo',
            'redo',
            'insertTable',
        ],
    },
    [CKPlugins.CustomFontColor]: CUSTOM_COLORS,
    [CKPlugins.CustomFontFamily]: CUSTOM_FONT_FAMILY,
    [CKPlugins.CustomFontBackgroundColor]: CUSTOM_BACKGROUND_COLORS,
    alignment: {
        options: [
            { name: 'left', className: 'ck-text-left' },
            { name: 'center', className: 'ck-text-center' },
            { name: 'right', className: 'ck-text-right' },
            { name: 'justify', className: 'ck-text-justify' }
        ]
    },
    table: {
        contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
    },
	htmlSupport: {
		allow: [
			{
				name: /^.*$/,
				styles: true,
				attributes: true,
				classes: true
			}
		]
	},
    fontBackgroundColor: {
        colorPicker: false
    },
    fontColor: {
        colorPicker: false
    },
	list: {
		properties: {
			styles: true,
			startIndex: true,
			reversed: true
		}
	},
    extraPlugins: [ImageInline, ClipboardLinksDecode],
    link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' },
    plugins: [...basePlugins, CustomFontColor, CustomFontFamily, CustomBackgroundFontColor],
};

export const configForGoogleCalendar = {
    toolbar: {
        items: [
            'bold',
            'italic',
            'underline',
            '|',
            'numberedList',
            'bulletedList',
            'link',
            '|',
            'undo',
            'redo',
        ],
    },
    extraPlugins: [ClipboardLinksDecode],
    link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' },
    plugins: basePlugins,
};

/**
 * Markdown mode: only constructs that Markdown can represent, and the editor data is Markdown instead of HTML.
 * Headings are mapped explicitly, otherwise CKEditor renders "Heading 1" as <h2> and a "#" would be saved as "##".
 */
export const configForMarkdown = {
    toolbar: {
        items: [
            'heading',
            '|',
            'bold',
            'italic',
            'strikethrough',
            'code',
            '|',
            'bulletedList',
            'numberedList',
            'blockQuote',
            'codeBlock',
            'horizontalLine',
            '|',
            'link',
            'insertTable',
            '|',
            'undo',
            'redo',
        ],
    },
    heading: {
        options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
        ],
    },
    // a Markdown table needs a header row, otherwise the editor falls back to saving a raw HTML <table>
    table: {
        contentToolbar: ['tableColumn', 'tableRow'],
        defaultHeadings: { rows: 1 },
    },
    link: { defaultProtocol: 'https://' },
    plugins: [
        Essentials,
        Paragraph,
        Heading,
        Bold,
        Italic,
        Strikethrough,
        Code,
        CodeBlock,
        List,
        BlockQuote,
        HorizontalLine,
        Link,
        AutoLink,
        Table,
        TableToolbar,
        Undo,
        SelectAll,
        Markdown,
        // typed ("# ", "- ", "**x**") or pasted plain-text Markdown becomes formatting instead of literal, escaped text
        Autoformat,
        PasteFromMarkdownExperimental,
    ],
};

export const configWithImageUpload = {
    ...defaultConfig
};

export default defaultConfig;
